import { Building2, Factory, ArrowRight } from 'lucide-react';
import AbbrTip from '@/components/AbbrTip';

// ReitTriangleRibbon — Round 74 (Feb 11, 2026)
// Extended Feb 26, 2026 (round 92k) to also fire for MLPs.
//
// Small contextual nudge that fires above the Triangle radar when the
// ticker is a REIT or MLP. Both vehicle types share the same problem:
// non-cash depreciation drags GAAP EPS, so the EPS leg of the
// Dividend Triangle looks weak even on a healthy name. The fix is the
// FFO-based Cash Lens panel on the Stock Metrics tab, which uses
// Net Income + D&A as a proxy for cash distribution coverage.
//
// Copy diverges between REITs and MLPs because the underlying
// vehicle structure and tax pass-through is different — REIT
// investors think in FFO, MLP unitholders think in DCF (Distributable
// Cash Flow). The math we surface is identical (NI + D&A); only the
// label changes.
//
// Renders nothing when neither flag is set, so it's safe to drop into
// any Triangle layout without conditional rendering at the call site.
//
// `insideModal` flips the copy from "open the Stock Modal" (when the
// ribbon fires on the standalone /triangle/:ticker page) to "on the
// Stock Metrics Tab" (when the ribbon fires on the Triangle tab WITHIN
// the open Stock Modal — telling the user to open a modal they're
// already inside is nonsensical).
export default function ReitTriangleRibbon({ isReit, isMlp, onOpenStockModal, insideModal = false }) {
  if (!isReit && !isMlp) return null;

  // REIT styling takes priority when both flags are somehow set (the
  // backend shouldn't allow that but cheap defense).
  const isReitView = !!isReit;
  const Icon = isReitView ? Building2 : Factory;
  const themeBorder = isReitView
    ? 'border-violet-300 dark:border-violet-800'
    : 'border-amber-300 dark:border-amber-800';
  const themeBg = isReitView
    ? 'bg-violet-50/70 dark:bg-violet-950/30'
    : 'bg-amber-50/70 dark:bg-amber-950/30';
  const themeIconColor = isReitView
    ? 'text-violet-600 dark:text-violet-300'
    : 'text-amber-600 dark:text-amber-300';
  const themeTextColor = isReitView
    ? 'text-violet-900 dark:text-violet-100'
    : 'text-amber-900 dark:text-amber-100';
  const themeCtaColor = isReitView
    ? 'text-violet-700 dark:text-violet-200 hover:text-violet-900 dark:hover:text-white'
    : 'text-amber-700 dark:text-amber-200 hover:text-amber-900 dark:hover:text-white';

  const lensName = isReitView ? 'REIT Cash Lens' : 'MLP Cash Lens';
  const cashMetricAbbr = isReitView ? 'FFO' : 'DCF';

  return (
    <div
      className={`rounded-lg border ${themeBorder} ${themeBg} px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1`}
      data-testid="reit-triangle-ribbon"
    >
      <Icon className={`w-4 h-4 ${themeIconColor} shrink-0`} />
      <p className={`text-xs leading-snug ${themeTextColor} flex-1 min-w-[200px]`}>
        {isReitView ? (
          <>
            <span className="font-bold"><AbbrTip term="REIT">REIT</AbbrTip> note:</span>
            {' '}the <AbbrTip term="EPS">EPS</AbbrTip> leg can look weak for a
            healthy REIT because depreciation is non-cash. Check the{' '}
            <span className="font-bold">{lensName}</span>{' '}
            {insideModal ? 'on the Stock Metrics Tab' : 'in the Stock Modal'}
            {' '}for the <AbbrTip term="FFO">{cashMetricAbbr}</AbbrTip>-based view that
            income investors actually use.
          </>
        ) : (
          <>
            <span className="font-bold"><AbbrTip term="MLP">MLP</AbbrTip> note:</span>
            {' '}MLPs measure distribution coverage against
            {' '}<AbbrTip term="DCF">{cashMetricAbbr}</AbbrTip>
            {' '}(Distributable Cash Flow), not GAAP <AbbrTip term="EPS">EPS</AbbrTip>.
            Heavy non-cash depreciation on pipelines drags reported earnings, so
            the EPS leg here often looks worse than the actual cash coverage.
            Check the <span className="font-bold">{lensName}</span>{' '}
            {insideModal ? 'on the Stock Metrics Tab' : 'in the Stock Modal'}
            {' '}for the cash-flow-based view.
          </>
        )}
      </p>
      {/* Only render the "Open Cash Lens" CTA when there's somewhere to
          open. Inside the modal the panel is already a tab-click away,
          and adding a button here would be redundant. */}
      {!insideModal && onOpenStockModal && (
        <button
          type="button"
          onClick={onOpenStockModal}
          className={`text-xs font-bold ${themeCtaColor} inline-flex items-center gap-1 underline-offset-2 hover:underline shrink-0`}
          data-testid="reit-ribbon-open-modal"
        >
          Open Cash Lens
          <ArrowRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
